import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useRouteError } from 'react-router'
import { translate, readLocale } from '../i18n'
import styles from './ErrorBoundary.module.css'

function CrashView({ error }: { error: unknown }) {
  // Rendered outside the provider tree on a crash, so it reads the saved language itself.
  const locale = readLocale(null)
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key)
  const detail = error instanceof Error ? error.message : ''
  return (
    <div className={styles.crash} role="alert">
      <p className="dateline">500</p>
      <h1>{t('crash.title')}</h1>
      <p>{t('crash.body')}</p>
      {detail ? <pre className={styles.detail}>{detail}</pre> : null}
      <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
        {t('crash.reload')}
      </button>
    </div>
  )
}

/** Router errorElement. */
export function RouteError() {
  return <CrashView error={useRouteError()} />
}

export class ErrorBoundary extends Component<{ children: ReactNode }, { error: unknown }> {
  state = { error: null as unknown }

  static getDerivedStateFromError(error: unknown) {
    return { error }
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('Unhandled UI error', error, info.componentStack)
  }

  render() {
    return this.state.error ? <CrashView error={this.state.error} /> : this.props.children
  }
}
